import React, { useEffect, useId, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { canUseCrmSearch, searchVisibilityRows } from '../../utils/searchRoleVisibility';
import { OPEN_CRM_SEARCH_EVENT } from './crmSearchEvents';
import './CrmSearchButton.css';

const CrmSearchButton: React.FC = () => {
  const { user } = useAuth();
  const [scopesOpen, setScopesOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const ignoreNextClick = useRef(false);
  const titleId = useId();
  const rows = searchVisibilityRows();

  useEffect(() => {
    if (!scopesOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setScopesOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setScopesOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [scopesOpen]);

  if (!canUseCrmSearch(user?.role, { is1099Contractor: user?.is1099Contractor === true })) return null;

  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const onContextMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isAdmin || !event.ctrlKey) return;
    event.preventDefault();
    ignoreNextClick.current = true;
    setScopesOpen((open) => !open);
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (ignoreNextClick.current) {
      ignoreNextClick.current = false;
      event.preventDefault();
      return;
    }
    if (isAdmin && event.ctrlKey) {
      event.preventDefault();
      setScopesOpen((open) => !open);
      return;
    }
    setScopesOpen(false);
    window.dispatchEvent(new Event(OPEN_CRM_SEARCH_EVENT));
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`crm-search-btn${scopesOpen ? ' active' : ''}`}
        title={isAdmin ? 'Search the CRM. Control-click to see who can see what.' : 'Search the CRM'}
        aria-label={isAdmin ? 'Search the CRM. Control-click to see who can see what.' : 'Search the CRM'}
        aria-expanded={isAdmin ? scopesOpen : undefined}
        aria-haspopup={isAdmin ? 'dialog' : undefined}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        <Search size={18} aria-hidden />
      </button>
      {scopesOpen && (
        <div
          ref={panelRef}
          className="crm-search-scopes"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
        >
          <div className="crm-search-scopes-head">
            <div>
              <h2 id={titleId}>Who can see what</h2>
              <p>A normal click searches as you. Control-click opens this list.</p>
            </div>
            <button type="button" className="crm-search-scopes-close" onClick={() => setScopesOpen(false)} aria-label="Close">
              <X size={16} />
            </button>
          </div>
          <div className="crm-search-scopes-table-wrap pm-scroll">
            <table>
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Search</th>
                  <th>Can see</th>
                  <th>Cannot see</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.role}</strong>
                      <span>{row.how}</span>
                    </td>
                    <td>{row.canSearch ? 'Yes' : 'Not yet'}</td>
                    <td>{row.sees.join(', ')}</td>
                    <td>{row.hidden.length ? row.hidden.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="crm-search-scopes-note">
            Clients, architects, engineers, designers, and suppliers are not in this search.
          </p>
        </div>
      )}
    </>
  );
};

export default CrmSearchButton;
